import { ApiLocation } from "@incendium/api";
import {
  Box,
} from "@mui/material";
import { locationService } from "Apis";
import {
  LocationsAccordionList,
  updateLocation,
  syncLocationPageTypes,
  getPageTypeChanges,
} from "features/location";
import { IOnboardStepProps } from "features/onboarding/types";
import { useLocations, useNotification } from "Hooks";
import Loading from "Components/Loading/Loading";
import { useConfirmation } from "Hooks/useConfirmation";
import produce from "immer";
import { useCallback } from "react";

function OnboardingLocations({

  project,
  nextDisabled,
}: IOnboardStepProps) {
  const { locations, setLocations, loading } = useLocations();
  const handleDelete = useConfirmation();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const onDelete = useCallback(
    (location: ApiLocation) => {
      handleDelete({
        title: `Are you sure you want to delete this location`,
        body: `This action can not be undone`,
        callback: async () => {
          await locationService.locationServiceDeleteLocation({
            projectId: project.id as number,
            locationId: location.id as number,
          });

          setLocations(
            produce(locations, (draft) => {
              const idx = locations.findIndex((l) => l.id === location.id);
              if (idx !== -1) draft.splice(idx, 1);
            })
          );

          return `${location.name} Deleted`;
        },
      });
    },
    [handleDelete, locations, project, setLocations]
  );

  const onSave = useCallback(
    async (location: ApiLocation) => {
      try {
        // Find the original location to compare page types
        const originalLocation = locations.find((l) => l.id === location.id);

        // Update the location itself (name, urlMatch, rules)
        const res = await updateLocation(project.id as number, location.id as number, location);

        // Sync page types if they've changed
        if (originalLocation) {
          const { toAdd, toRemove } = getPageTypeChanges(
            originalLocation.pageTypes || [],
            location.pageTypes || []
          );

          if (toAdd.length > 0 || toRemove.length > 0) {
            await syncLocationPageTypes(
              project.id as number,
              location.id as number,
              originalLocation.pageTypes || [],
              location.pageTypes || []
            );
          }
        }

        setLocations(produce(locations, (draft) => {
          const idx = locations.findIndex((l) => l.id === location.id);
          if (idx !== -1) draft[idx] = res as ApiLocation;
        }));
        showSuccessNotification(`${location.name} Updated`);
      } catch (error) {
        showErrorNotification(`Failed to update ${location.name}`);
      }

    },
    [locations, setLocations, project, showSuccessNotification, showErrorNotification]
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box sx={{ flex: 1 }}>
        <LocationsAccordionList
          locations={locations}
          onDelete={onDelete}
          onSave={onSave}
        />
      </Box>
    </Box>
  );
}

export default OnboardingLocations;

